import { Block, type Change, type Inline, type NodeType, Text, type Value } from '@gitbook/slate';
import { assert, assertNever } from '@gitbook/util-ts';

import { createParagraph } from '../paragraph';
import { BLOCK_HEADING_2, BLOCK_STEPPER, BLOCK_STEPPER_STEP } from '../types';

/**
 * Create a stepper block.
 */
export function createStepper(heading?: Array<Inline | Text>): Block {
    return Block.create({
        type: BLOCK_STEPPER,
        nodes: [createStepperStep(heading), createStepperStep()],
    });
}

/**
 * Create a stepper step block.
 */
export function createStepperStep(heading?: Array<Inline | Text>, nodes?: Array<Block>): Block {
    return Block.create({
        type: BLOCK_STEPPER_STEP,
        nodes: [
            Block.create({
                type: BLOCK_HEADING_2,
                nodes: heading || [Text.create('')],
            }),
            ...(nodes || [createParagraph()]),
        ],
    });
}

/**
 * Return the currently selected selectable block.
 */
export function getCurrentStepper(value: Value):
    | undefined
    | {
          stepper: Block;
          step: Block;
      } {
    assert(value.startKey, 'Value must have a start key');
    const step = value.document.getClosest(
        value.startKey,
        (node: NodeType) => node.object === 'block' && node.type === BLOCK_STEPPER_STEP
    ) as Block | undefined;

    const stepper = step ? value.document.getParent(step.key) : null;

    if (!step || stepper?.object !== 'block') {
        return undefined;
    }

    return {
        stepper,
        step,
    };
}

/**
 * Insert a new stepper step at a given index.
 */
export function insertStepperStepAt(change: Change, stepperBlockKey: string, index: number) {
    const newStep = createStepperStep();
    change.insertNodeByKey(stepperBlockKey, index, newStep);
    change.collapseToStartOf(newStep);
    change.focus();
}

/**
 * Move a a stepper step up.
 */
export function moveStepperStep(
    change: Change,
    stepperStepBlockKey: string,
    direction: 'up' | 'down'
) {
    const step = change.value.document.assertBlock(stepperStepBlockKey);
    const stepper = change.value.document.getParent(stepperStepBlockKey);
    assert(step, 'Stepper step must exist');
    assert(stepper, 'Stepper step must have a parent');

    switch (direction) {
        case 'up': {
            const previousStep = stepper.getPreviousSibling(stepperStepBlockKey);
            if (!previousStep) {
                return;
            }
            change.moveBeforeNodeByKey(step.key, previousStep.key);
            break;
        }
        case 'down': {
            const nextStep = stepper.getNextSibling(stepperStepBlockKey);
            if (!nextStep) {
                return;
            }
            change.moveAfterNodeByKey(step.key, nextStep.key);
            break;
        }
        default:
            assertNever(direction);
    }
    change.focus();
}
